import { useState, useEffect } from "react";
import { FaEye, FaEyeSlash, FaInfoCircle, FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST NOTIFICATION COMPONENT — Temple Themed
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  // Using translation for toast labels
  const { t } = useTranslation();
  
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: {
      icon: <FaCheck />,
      label: t("toastBlessings"),
      accentColor: "#2a6b2a",
      accentLight: "rgba(42,107,42,0.15)",
      iconBg: "rgba(42,107,42,0.12)",
      borderColor: "rgba(42,107,42,0.3)",
      topBarColor: "#2a6b2a",
      symbol: "✦"
    },
    error: {
      icon: <FaTimes />,
      label: t("toastLoginFail"),
      accentColor: "#3d0c0c",
      accentLight: "rgba(139,26,26,0.12)",
      iconBg: "rgba(139,26,26,0.1)",
      borderColor: "rgba(139,26,26,0.28)",
      topBarColor: "#3d0c0c",
      symbol: "✗"
    },
    info: {
      icon: <FaInfoCircle />,
      label: t("toastDivineNotice"),
      accentColor: "#8a6020",
      accentLight: "rgba(201,151,58,0.1)",
      iconBg: "rgba(201,151,58,0.1)",
      borderColor: "rgba(201,151,58,0.3)",
      topBarColor: "#c9973a",
      symbol: "ॐ"
    }
  };

  const c = config[type] || config.info;

  return (
    <div className="toast-container">
      <div className="toast-card" style={{ "--accent": c.accentColor, "--accent-light": c.accentLight, "--border-color": c.borderColor, "--top-bar": c.topBarColor }}>
        {/* Top accent bar */}
        <div className="toast-top-bar" />

        {/* Corner ornaments */}
        <span className="toast-corner tl" />
        <span className="toast-corner tr" />
        <span className="toast-corner bl" />
        <span className="toast-corner br" />

        {/* Icon medallion */}
        <div className="toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="toast-medallion-ring" />
          <div className="toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>

        {/* Content */}
        <div className="toast-content">
          <div className="toast-label-row">
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="toast-rule" style={{ background: `linear-gradient(90deg, transparent, ${c.accentColor}55, transparent)` }} />
          <p className="toast-message">{message}</p>
        </div>

        {/* Close */}
        <button className="toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>

        {/* Bottom gold rule */}
        <div className="toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   ILLUSTRATOR PANEL
───────────────────────────────────────────────────── */
function IllustratorPanel({ illusSymbol }) {
  const { t } = useTranslation();
  return (
    <div className="illus-panel">
      <div className="illus-rings">
        {[280, 230, 185, 145, 110].map((s, i) => (
          <div key={s} className="illus-ring"
            style={{ width: s, height: s, animationDelay: `${i * 0.4}s` }} />
        ))}
      </div>

      <div className="illus-medallion">
        <div className="medallion-outer-ring" />
        <div className="medallion-inner">
          <div className={`medallion-face ${illusSymbol === "om" ? "visible" : ""}`}>
            <span className="illus-om">ॐ</span>
          </div>
          <div className={`medallion-face ${illusSymbol === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple Logo" className="illus-logo-img" />
          </div>
        </div>
        <div className="medallion-halo" />
      </div>

      <div className="illus-info">
        <div className="illus-rule" />
        {/* Using Footer translations for consistency */}
        <div className="illus-name">{t("ftTempleName")}</div>
        <div className="illus-sub">{t("ftTempleSub")}</div>
        <div className="illus-loc">✦ {t("templeLoc")} ✦</div>
        <div className="illus-rule" />
      </div>

      <div className="illus-quote">
        {/* Reusing existing quote translation or creating specific one if needed. Using generic text if key missing, or hpQuote */}
        <p>{t("hpQuote")}</p>
      </div>

      <div className="illus-dots">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="illus-dot" style={{ opacity: i === 2 ? 0.7 : 0.25 }} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PAGE SHELL
───────────────────────────────────────────────────── */
function PageShell({ children, illusSymbol }) {
  return (
    <div className="login-page">
      <div className="outer-card">
        <div className="outer-card-strip" />
        <div className="login-split">
          <div className="login-left">
            <IllustratorPanel illusSymbol={illusSymbol} />
          </div>
          <div className="login-right">{children}</div>
        </div>
        <div className="outer-card-strip bottom" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   FORM CARD
───────────────────────────────────────────────────── */
function FormCard({ title, subtitle, children }) {
  return (
    <div className="form-card">
      <div className="form-header">
        <div className="fh-icon">
          <div className="fh-ring" />
          <span className="fh-om">ॐ</span>
        </div>
        <h2 className="form-title">{title}</h2>
        {subtitle && <p className="form-subtitle">{subtitle}</p>}
        <div className="form-title-rule" />
      </div>
      <div className="form-body">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN LOGIN COMPONENT
───────────────────────────────────────────────────── */
export default function Login() {
  const navigate = useNavigate();
  // 5️⃣ Use Translation Hook
  const { t } = useTranslation();
  
  const [view, setView]               = useState("login");
  const [mobile, setMobile]           = useState("");
  const [password, setPassword]       = useState("");
  const [showPass, setShowPass]       = useState(false);
  const [forgotMob, setForgotMob]     = useState("");
  const [otp, setOtp]                 = useState(["", "", "", "", "", ""]);
  const [newPass, setNewPass]         = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNew, setShowNew]         = useState(false);
  const [showConf, setShowConf]       = useState(false);
  const [loading, setLoading]         = useState(false);
  const [toast, setToast]             = useState(null);
  const [illusSymbol, setIllusSymbol] = useState("om");

  /* Alternate OM ↔ logo every 3 s */
  useEffect(() => {
    const t = setInterval(() => setIllusSymbol(s => s === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(t);
  }, []);

  const handleOtpChange = (val, idx) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
  };
  const handleOtpKey = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0)
      document.getElementById(`otp-${idx - 1}`)?.focus();
  };

  const passStrength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p) && /[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const strength      = passStrength(newPass);
  const strengthLabel = ["", t("strengthWeak"), t("strengthFair"), t("strengthGood"), t("strengthStrong")][strength];
  const strengthColor = ["", "#b52020", "#c9973a", "#e8c46a", "#2a6b2a"][strength];

  // LOGIN SUBMIT HANDLER
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if(!mobile || !password) {
      setToast({ message: t("errLoginRequired"), type: "info" });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, password })
      });
      const data = await res.json();

      if (res.ok) {
        setToast({ message: t("toastLoginSuccess"), type: "success" });
        
        // --- STORE SESSION / TOKEN ---
        // 1. Store the secure token (used for API requests later)
        localStorage.setItem('authToken', data.token);
        
        // 2. Store user details (used for UI display like "Welcome, [Name]")
        localStorage.setItem('user', JSON.stringify(data.user));

        // Role-based Redirection
        if (data.user.role === 'admin') {
          setTimeout(() => navigate('/admin'), 1000); 
        } else {
          setTimeout(() => navigate('/home'), 1000);
        }
      } else {
        setToast({ message: data.error || t("toastLoginFail"), type: "error" });
      }
    } catch (err) {
      setToast({ message: t("errServer"), type: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ══ LOGIN VIEW ══ */
  if (view === "login") return (
    <>
      <PageStyles />
      <Navbar />
      <PageShell illusSymbol={illusSymbol}>
        <FormCard title={t("loginTitle")} subtitle={t("loginSubtitle")}>

          <div className="input-group">
            <label className="input-label">{t("lblMobile")}</label>
            <div className="input-wrap">
              <span className="input-prefix">🇮🇳 +91</span>
              <div className="input-sep" />
              <input
                className="form-input"
                type="tel"
                maxLength={10}
                placeholder={t("phMobile")}
                value={mobile}
                onChange={e => setMobile(e.target.value.replace(/\D/g, ""))}
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">{t("lblPassword")}</label>
            <div className="input-wrap">
              <input
                className="form-input"
                type={showPass ? "text" : "password"}
                placeholder={t("phPassword")}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleLogin(e); }}
              />
              <button className="eye-btn" onClick={() => setShowPass(p => !p)}>
                {showPass ? <FaEyeSlash  /> : <FaEye />}
              </button>
            </div>
          </div>

          <button className="submit-btn" onClick={handleLogin} disabled={loading}>
            <span>{loading ? t("btnAuthenticating") : t("btnLogin")}</span>
            <span className="btn-icon">›</span>
          </button>

          <div className="form-divider">
            <span className="divider-line" />
            <span className="divider-text">{t("or")}</span>
            <span className="divider-line" />
          </div>

          <p className="form-footer-text">
            {t("newDevotee")}{" "}
            <button className="text-link" onClick={() => navigate('/register')}>{t("createAccount")}</button>
          </p>

        </FormCard>
      </PageShell>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );

  /* ══ FORGOT PASSWORD VIEW ══ */
  if (view === "forgot") return (
    <>
      <PageStyles />
      <Navbar />
      <PageShell illusSymbol={illusSymbol}>
        <FormCard title={t("resetTitle")} subtitle={t("resetSubtitle")}>
          <div className="info-banner">
            <span className="info-icon">🔒</span>
            <span className="info-text">{t("resetInfo")}</span>
          </div>
          <div className="input-group">
            <label className="input-label">{t("lblRegMobile")}</label>
            <div className="input-wrap">
              <span className="input-prefix">🇮🇳 +91</span>
              <div className="input-sep" />
              <input className="form-input" type="tel" maxLength={10} placeholder={t("phMobile")} value={forgotMob} onChange={e => setForgotMob(e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <button className="submit-btn" onClick={() => setView("forgot-otp")}><span>{t("btnSendOtp")}</span><span className="btn-icon">›</span></button>
          <button className="ghost-btn" onClick={() => setView("login")}>‹ {t("backToLogin")}</button>
        </FormCard>
      </PageShell>
      <Footer />
    </>
  );

  /* ══ VERIFY OTP VIEW ══ */
  if (view === "forgot-otp") return (
    <>
      <PageStyles />
      <Navbar />
      <PageShell illusSymbol={illusSymbol}>
        <FormCard title={t("verifyTitle")} subtitle={`${t("verifySubtitle")} +91 ${forgotMob || "XXXXXXXXXX"}`}>
          <div className="input-group">
            <label className="input-label">{t("lblOtp")}</label>
            <div className="otp-row">
              {otp.map((d, i) => (
                <input key={i} id={`otp-${i}`} className="otp-box" type="text" inputMode="numeric" maxLength={1} value={d} onChange={e => handleOtpChange(e.target.value, i)} onKeyDown={e => handleOtpKey(e, i)} />
              ))}
            </div>
            <div className="otp-meta">
              <span className="input-hint">{t("validFor")}</span>
              <button className="text-link small">{t("resendOtp")}</button>
            </div>
          </div>
          <button className="submit-btn" onClick={() => setView("reset")}><span>{t("btnVerify")}</span><span className="btn-icon">›</span></button>
          <button className="ghost-btn" onClick={() => setView("forgot")}>‹ Back</button>
        </FormCard>
      </PageShell>
      <Footer />
    </>
  );

  /* ══ RESET PASSWORD VIEW ══ */
  if (view === "reset") return (
    <>
      <PageStyles />
      <Navbar />
      <PageShell illusSymbol={illusSymbol}>
        <FormCard title={t("newPassTitle")} subtitle={t("newPassSubtitle")}>
          <div className="input-group">
            <label className="input-label">{t("lblNewPass")}</label>
            <div className="input-wrap">
              <input className="form-input" type={showNew ? "text" : "password"} placeholder={t("phNewPass")} value={newPass} onChange={e => setNewPass(e.target.value)} />
              <button className="eye-btn" onClick={() => setShowNew(p => !p)}>{showNew ? <FaEyeSlash  /> : <FaEye />}</button>
            </div>
            {newPass && (
              <div className="strength-wrap">
                <div className="strength-bars">
                  {[1, 2, 3, 4].map(n => (
                    <div key={n} className="strength-bar" style={{ background: n <= strength ? strengthColor : "rgba(74,26,8,0.1)" }} />
                  ))}
                </div>
                <span className="strength-label" style={{ color: strengthColor }}>{strengthLabel}</span>
              </div>
            )}
          </div>
          <div className="input-group">
            <label className="input-label">{t("lblConfirmPass")}</label>
            <div className="input-wrap">
              <input className="form-input" type={showConf ? "text" : "password"} placeholder={t("phConfirmPass")} value={confirmPass} onChange={e => setConfirmPass(e.target.value)} />
              <button className="eye-btn" onClick={() => setShowConf(p => !p)}>{showConf ? <FaEyeSlash  /> : <FaEye />}</button>
            </div>
            {confirmPass && (
              <span className={`input-feedback ${newPass === confirmPass ? "success" : "error"}`}>
                {newPass === confirmPass ? `✓ ${t("passMatch")}` : `✗ ${t("passMismatch")}`}
              </span>
            )}
          </div>
          <button className="submit-btn" disabled={!newPass || newPass !== confirmPass || newPass.length < 6} onClick={() => setView("login")}><span>{t("btnSetPass")}</span><span className="btn-icon">›</span></button>
        </FormCard>
      </PageShell>
      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════ */
function PageStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep:  #1a0404;
        --crimson-mid:   #2c0808;
        --crimson-rich:  #3d0c0c;
        --gold-bright:   #e8c46a;
        --gold-mid:      #c9973a;
        --gold-dim:      #8a6020;
        --gold-pale:     #f5e4b0;
        --parchment:     #FDF3E3;
        --text-dark:     #1a0404;
        --text-mid:      #4a1a08;
      }

      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
      body { background: radial-gradient(ellipse at 30% 0%, rgba(201,151,58,0.07) 0%, transparent 60%), var(--parchment); min-height: 100vh; font-family: 'EB Garamond', serif; }

      /* ═══ PAGE ═══ */
      .login-page { background: radial-gradient(ellipse at 30% 0%, rgba(201,151,58,0.07) 0%, transparent 60%), var(--parchment); min-height: calc(100vh - 160px); display: flex; align-items: center; justify-content: center; padding: 48px 24px; }
      
      /* ═══ OUTER CARD ═══ */
      .outer-card { width: 100%; max-width: 1020px; border: 1px solid rgba(201,151,58,0.3); box-shadow: 0 0 0 4px rgba(201,151,58,0.06), 0 0 0 8px rgba(201,151,58,0.03), 0 32px 80px rgba(26,4,4,0.22), 0 8px 24px rgba(26,4,4,0.12); clip-path: polygon(0 20px, 20px 0, calc(100% - 20px) 0, 100% 20px, 100% calc(100% - 20px), calc(100% - 20px) 100%, 20px 100%, 0 calc(100% - 20px)); overflow: hidden; animation: outerCardIn 0.6s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes outerCardIn { from { opacity: 0; transform: scale(0.97) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      
      .outer-card-strip { height: 3px; background: linear-gradient(90deg, transparent, var(--gold-dim) 10%, var(--gold-bright) 30%, #fff8e7 50%, var(--gold-bright) 70%, var(--gold-dim) 90%, transparent); }
      .outer-card-strip.bottom { background: linear-gradient(90deg, transparent, var(--gold-dim) 15%, var(--gold-mid) 50%, var(--gold-dim) 85%, transparent); }

      /* ═══ SPLIT GRID ═══ */
      .login-split { display: grid; grid-template-columns: 1fr 1fr; min-height: 580px; }

      /* ═══ LEFT PANEL ═══ */
      .login-left { background: radial-gradient(ellipse at 50% 25%, rgba(120,25,25,0.55) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(60,10,10,0.4) 0%, transparent 50%), linear-gradient(160deg, #2c0a0a 0%, #1a0404 55%, #0e0101 100%); display: flex; align-items: center; justify-content: center; padding: 52px 36px; position: relative; overflow: hidden; border-right: 1px solid rgba(201,151,58,0.15); }
      .login-left::before { content: ''; position: absolute; inset: 0; background-image: url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E"); opacity: 0.5; pointer-events: none; }

      /* ═══ RIGHT PANEL ═══ */
      .login-right { background: var(--parchment); display: flex; align-items: center; justify-content: center; padding: 52px 44px; position: relative; overflow: hidden; }

      /* ═══ ILLUSTRATOR ═══ */
      .illus-panel { position: relative; display: flex; flex-direction: column; align-items: center; z-index: 1; width: 100%; max-width: 340px; }
      .illus-rings { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); width: 290px; height: 290px; pointer-events: none; }
      .illus-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.08); animation: gentlePulse 4s ease-in-out infinite alternate; }
      .illus-ring:nth-child(odd) { border-style: dashed; }
      @keyframes gentlePulse { from { opacity: 0.4; } to { opacity: 0.9; } }

      .illus-medallion { width: 110px; height: 110px; position: relative; display: flex; align-items: center; justify-content: center; margin-bottom: 28px; margin-top: 40px; flex-shrink: 0; }
      .medallion-outer-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 40deg, var(--gold-mid) 80deg, transparent 120deg, var(--gold-bright) 160deg, transparent 200deg, var(--gold-mid) 240deg, transparent 280deg, var(--gold-bright) 320deg, transparent 360deg) border-box; border: 2px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 10s linear infinite; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .medallion-inner { position: absolute; inset: 10px; border-radius: 50%; background: radial-gradient(circle, #3d1010 0%, #1a0404 70%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.25); box-shadow: inset 0 0 20px rgba(0,0,0,0.7), 0 0 20px rgba(232,196,106,0.08); overflow: hidden; display: flex; align-items: center; justify-content: center; }
      .medallion-face { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.6s ease, transform 0.6s ease; border-radius: 50%; }
      .medallion-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .illus-om { font-family: 'Cinzel Decorative', cursive; font-size: 36px; background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 2px 8px rgba(232,196,106,0.6)); }
      .illus-logo-img { width: 62px; height: 62px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .medallion-halo { position: absolute; inset: -8px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.12) 0%, transparent 70%); pointer-events: none; }
      .illus-info { display: flex; flex-direction: column; align-items: center; gap: 5px; margin-bottom: 20px; width: 100%; }
      .illus-rule { width: 70%; height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35), transparent); margin: 6px 0; }
      .illus-name { font-family: 'Cinzel Decorative', cursive; font-size: 15px; font-weight: 900; letter-spacing: 1px; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; text-align: center; line-height: 1.4; }
      .illus-sub { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.5); text-align: center; }
      .illus-loc { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.3); text-align: center; }
      .illus-quote { margin-bottom: 20px; padding: 0 8px; }
      .illus-quote p { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(245,228,176,0.4); text-align: center; line-height: 1.75; }
      .illus-dots { display: flex; gap: 8px; align-items: center; }
      .illus-dot { width: 5px; height: 5px; background: var(--gold-bright); transform: rotate(45deg); display: block; }

      /* ═══ FORM CARD ═══ */
      .form-card { width: 100%; max-width: 600px; position: relative; z-index: 1; }
      .form-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; pointer-events: none; }
      .form-header { text-align: center; padding: 32px 32px 0; }
      .fh-icon { width: 54px; height: 54px; position: relative; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
      .fh-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 60deg, transparent 120deg, var(--gold-mid) 180deg, transparent 240deg, var(--gold-bright) 300deg, transparent 360deg) border-box; border: 1.5px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 8s linear infinite; }
      .fh-om { font-family: 'Cinzel Decorative', cursive; font-size: 18px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; position: relative; z-index: 1; }
      .form-title { font-family: 'Cinzel Decorative', cursive; font-size: 17px; font-weight: 900; letter-spacing: 0.5px; background: linear-gradient(135deg, #3d0c0c, var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 6px; }
      .form-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.55); line-height: 1.5; }
      .form-title-rule { width: 60%; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); margin: 16px auto 0; }
      .form-body { padding: 24px 32px 36px; display: flex; flex-direction: column; gap: 16px; }
      .input-group { display: flex; flex-direction: column; gap: 7px; }
      .label-row { display: flex; justify-content: space-between; align-items: center; }
      .input-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .input-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); transition: border-color 0.3s, box-shadow 0.3s, background 0.3s; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; }
      .input-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .input-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; white-space: nowrap; flex-shrink: 0; }
      .input-sep { width: 1px; height: 20px; background: rgba(201,151,58,0.2); flex-shrink: 0; }
      .form-input { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-dark); padding: 13px 20px 13px 14px; letter-spacing: 0.3px; }
      .form-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; font-size: 14px; }
      .input-wrap .form-input:first-child { padding-left: 20px; }
      .eye-btn { background: none; border: none; cursor: pointer; padding: 13px 18px; font-size: 14px; color: rgba(74,26,8,0.35); transition: color 0.2s; flex-shrink: 0; }
      .eye-btn:hover { color: var(--gold-mid); }
      .input-hint { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12px; color: rgba(74,26,8,0.4); }
      .input-feedback { font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }
      .input-feedback.error { color: #b52020; }
      .input-feedback.success { color: #2a6b2a; }
      
      /* ── OTP ── */
      .otp-row { display: flex; gap: 9px; justify-content: center; }
      .otp-box { width: 46px; height: 52px; text-align: center; font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 700; color: var(--text-dark); background: #fff9f0; border: 1px solid rgba(201,151,58,0.3); outline: none; transition: all 0.25s ease; clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px); caret-color: var(--text-dark); }
      .otp-box:focus { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.15); }
      .otp-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; }
      
      /* ── Strength ── */
      .strength-wrap { display: flex; align-items: center; gap: 10px; margin-top: 2px; }
      .strength-bars { display: flex; gap: 5px; flex: 1; }
      .strength-bar { flex: 1; height: 3px; border-radius: 2px; transition: background 0.3s; clip-path: polygon(3px 0%, 100% 0%, calc(100% - 3px) 100%, 0% 100%); }
      .strength-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; flex-shrink: 0; transition: color 0.3s; min-width: 38px; text-align: right; }

      /* ── Buttons ── */
      .submit-btn { font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 15px 28px; cursor: pointer; width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; margin-top: 6px; }
      .submit-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .submit-btn:hover::before { left: 130%; }
      .submit-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .submit-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .btn-icon { font-size: 18px; font-family: serif; line-height: 1; }
      .ghost-btn { font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(74,26,8,0.5); background: transparent; border: 1px solid rgba(201,151,58,0.22); padding: 12px 20px; cursor: pointer; width: 100%; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); transition: all 0.3s ease; }
      .ghost-btn:hover { border-color: rgba(201,151,58,0.45); color: var(--text-mid); background: rgba(201,151,58,0.05); }
      .text-link { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-dim); background: none; border: none; cursor: pointer; padding: 0; transition: color 0.2s; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(138,96,32,0.3); }
      .text-link:hover { color: var(--gold-mid); }
      .text-link.small { font-size: 9px; }
      
      /* ── Divider ── */
      .form-divider { display: flex; align-items: center; gap: 12px; }
      .divider-line { flex: 1; height: 1px; background: rgba(201,151,58,0.2); }
      .divider-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(74,26,8,0.3); }
      
      /* ── Info banner ── */
      .info-banner { display: flex; gap: 12px; align-items: flex-start; padding: 13px 16px; background: rgba(201,151,58,0.07); border: 1px solid rgba(201,151,58,0.18); clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px)); }
      .info-icon { font-size: 15px; flex-shrink: 0; margin-top: 1px; }
      .info-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13.5px; color: rgba(74,26,8,0.6); line-height: 1.55; }
      .form-footer-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.42); text-align: center; }

      /* ══════════════════════════════════════════
         TOAST — Temple Sacred Parchment Theme
      ══════════════════════════════════════════ */
      .toast-container {
        position: fixed;
        top: 28px;
        right: 28px;
        z-index: 2000;
        animation: toastSlideIn 0.45s cubic-bezier(0.23, 1, 0.32, 1) both;
      }

      @keyframes toastSlideIn {
        from { opacity: 0; transform: translateX(60px) scale(0.95); }
        to   { opacity: 1; transform: translateX(0)   scale(1);    }
      }

      .toast-card {
        position: relative;
        width: 340px;
        background: linear-gradient(145deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%);
        border: 1px solid var(--border-color);
        padding: 0 0 14px 0;
        overflow: hidden;
        margin-top:20%;

        /* Clipped corners — octagonal feel matching the form inputs */
        clip-path: polygon(
          0 12px, 12px 0,
          calc(100% - 12px) 0, 100% 12px,
          100% calc(100% - 12px), calc(100% - 12px) 100%,
          12px 100%, 0 calc(100% - 12px)
        );

        box-shadow:
          0 4px 6px rgba(26,4,4,0.06),
          0 12px 32px rgba(26,4,4,0.14),
          0 0 0 1px rgba(201,151,58,0.08),
          inset 0 1px 0 rgba(255,255,255,0.7);
      }

      /* Coloured top accent bar */
      .toast-top-bar {
        height: 3px;
        background: linear-gradient(
          90deg,
          transparent 0%,
          var(--accent) 20%,
          var(--accent) 80%,
          transparent 100%
        );
        margin-bottom: 16px;
      }

      /* Gold corner ornaments */
      .toast-corner {
        position: absolute;
        width: 10px;
        height: 10px;
        border-color: rgba(201,151,58,0.45);
        border-style: solid;
      }
      .toast-corner.tl { top: 12px;  left: 12px;  border-width: 1px 0 0 1px; }
      .toast-corner.tr { top: 12px;  right: 12px; border-width: 1px 1px 0 0; }
      .toast-corner.bl { bottom: 8px; left: 12px;  border-width: 0 0 1px 1px; }
      .toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }

      /* Body row */
      .toast-card > .toast-medallion,
      .toast-card > .toast-content,
      .toast-card > .toast-close {
        /* inline layout handled below */
      }

      /* Icon medallion */
      .toast-medallion {
        position: absolute;
        left: 18px;
        top: 26px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .toast-medallion-ring {
        position: absolute;
        inset: -3px;
        border-radius: 50%;
        border: 1px dashed rgba(201,151,58,0.3);
        animation: toastSpin 8s linear infinite;
      }
      @keyframes toastSpin { to { transform: rotate(360deg); } }
      .toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }

      /* Content — offset to clear the medallion */
      .toast-content {
        padding: 0 40px 0 66px;
      }

      .toast-label-row {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 5px;
      }
      .toast-symbol {
        font-family: 'Cinzel Decorative', cursive;
        font-size: 9px;
        opacity: 0.7;
      }
      .toast-title {
        font-family: 'Cinzel', serif;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
        flex: 1;
      }

      .toast-rule {
        height: 1px;
        margin-bottom: 7px;
      }

      .toast-message {
        font-family: 'EB Garamond', serif;
        font-style: italic;
        font-size: 14px;
        color: rgba(74,26,8,0.75);
        line-height: 1.5;
      }

      /* Bottom gold rule */
      .toast-bottom-rule {
        height: 1px;
        margin: 12px 20px 0;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent);
      }

      /* Close button */
      .toast-close {
        position: absolute;
        top: 22px;
        right: 14px;
        background: none;
        border: none;
        font-size: 13px;
        cursor: pointer;
        opacity: 0.45;
        line-height: 1;
        padding: 2px 4px;
        transition: opacity 0.2s;
        font-family: sans-serif;
      }
      .toast-close:hover { opacity: 0.9; }

      /* ═══ RESPONSIVE ═══ */
      @media (max-width: 860px) {
        .login-page { padding: 0; min-height: calc(100vh - 160px); align-items: stretch; background: var(--parchment); }
        .outer-card { clip-path: none; border: none; box-shadow: none; width: 100%; }
        .login-split { grid-template-columns: 1fr; min-height: calc(100vh - 166px); }
        .login-left { display: none; }
        .login-right { padding: 40px 24px; background: transparent; }
        .form-card { clip-path: none; border-radius: 2px; max-width: 100%; }
        .outer-card-strip { display: none; }
        .toast-container { top: 16px; right: 16px; }
        .toast-card { width: 300px; }
      }
      @media (max-width: 480px) {
        .login-right { padding: 28px 16px; }
        .form-body { padding: 20px 18px 28px; gap: 14px; }
        .form-header { padding: 24px 18px 0; }
        .form-title { font-size: 15px; }
        .otp-box { width: 40px; height: 46px; font-size: 17px; }
        .otp-row { gap: 7px; }
        .toast-card { width: 280px; }
      }
    `}</style>
  );
}